import {
  useAckAlarm,
  useAlarmRecipients,
  useAlarms,
  useClearAlarm,
  useClearAlarms,
} from '@ctcells/api'
import { dataSourceFor } from '@ctcells/core'
import { errorMessage, t } from '@ctcells/i18n'

import { DEMO_ALARMS } from '../demoData.ts'
import { DetailPage } from '../resourceParts.tsx'
import { useScreenAccess } from '../screenAccess.ts'
import { ScreenLoading, ScreenState } from '../ScreenState.tsx'

import { AlarmDetailScreen } from './AlarmDetailScreen.tsx'
import { ALARM_SNAPSHOT_ARGS } from './alarmsQuery.ts'
import { summarizeClearAll } from './alarmBulkClear.ts'
import { AlarmsScreen } from './AlarmsScreen.tsx'

/**
 * 알람 센터 컨테이너.
 *
 * 🔴 **2026-09-20 `live` 로 올렸다.** 종전에는 「탭 3종을 가를 `category` 가 없다」를 사유로
 *    `demo` 였고, 그때는 저장 표에 열조차 없어 **탭 축을 서버 질의 축(`state`)으로 바꾸고 올렸다.**
 * 🔵 **2026-09-26 정정 — `category` 가 들어왔다**(마이그레이션 `0014`·`0016` · `raise_alarm` 이 적재 때 채움).
 *    화면이 툴바 「분류」 선택기로 가른다. **질의는 그대로 한 벌**이다 — `?category=` 로 좁혀 받으면
 *    헤더 배지와 캐시가 갈린다(`alarmsQuery.ts`). 받은 목록에서 거르고 센다.
 *
 * 실물로 되는 것(2026-09-20 dev-operator 로 확인): 목록 · 확인 · 해제 전부.
 * `POST /alarms/{id}/ack` · `/clear` 는 **제어락 비대상**이다(계약에 `ControlToken` 파라미터 없음).
 *
 * 🔴 **낙관적 전이가 없다.** `ack` 은 200 만 받고 화면을 직접 고치지 않는다 —
 *   목록은 명령 훅이 무효화한 뒤 서버가 준 값으로 다시 그린다(DP-04).
 * 🔴 **구독이 와도 화면이 알람을 지우지 않는다**(DP-02). `alarm` 프레임은 `stream/bridge.ts` 가
 *   **캐시 무효화만** 한다 — 확인·해제는 사람이 누를 때만 난다.
 *
 * 🔴 **프롭이 없다**(2026-09-22). 종전에는 라우트가 `opsTab="alarms"` 를 넘겨 「작업」 무리 탭
 *    줄을 켰는데, eric 지시(「작업에서 알람탭을 따로 떼줘」)로 알람이 **헤더의 제 문**이 됐다.
 *    무리가 없으니 넘길 탭도 없다 — 되살리지 마라.
 */
export interface AlarmsContainerProps {
  /**
   * 오른쪽 서랍 모드(통합 콘솔 `/alarms/:alarmId` · eric 2026-09-28). 넘기면 행이 서랍을 연다.
   * 🔴 안 넘기면 종전 그대로(줄 아래 펼침) — 장비 관점 콘솔은 이 프롭 없이 부른다.
   */
  onOpenAlarm?: ((alarmId: string) => void) | undefined
  selectedAlarmId?: string | undefined
}

export function AlarmsContainer(props: AlarmsContainerProps = {}) {
  return dataSourceFor('alarms') === 'demo' ? <AlarmsDemo {...props} /> : <AlarmsLive {...props} />
}

/** 명령 배선은 데모에서도 같은 훅을 쓴다 — 전환 때 바뀌는 코드가 없게 하려는 것이다 */
function useAlarmCommands() {
  const ack = useAckAlarm()
  const clear = useClearAlarm()
  const clearAll = useClearAlarms()
  return {
    onClearAll: (alarmIds: string[], note: string | undefined) => {
      clearAll.mutate({ alarmIds, ...(note === undefined ? {} : { note }) })
    },
    onClearAllReset: () => {
      clearAll.reset()
    },
    clearAll: {
      pending: clearAll.isPending,
      ...(clearAll.data === undefined ? {} : { summary: summarizeClearAll(clearAll.data) }),
      ...(clearAll.error === null ? {} : { error: errorMessage(clearAll.error) }),
    },
    onAck: (alarmId: string) => {
      ack.mutate({ alarmId })
    },
    onClear: (alarmId: string) => {
      clear.mutate({ alarmId })
    },
  }
}

function AlarmsDemo(props: AlarmsContainerProps) {
  const access = useScreenAccess()
  const commands = useAlarmCommands()
  // 목록 props 를 안 넘긴다 = 화면 기본값(DEMO_ALARMS · DEMO_RESOURCE_KPI)이 그대로 산다
  return <AlarmsScreen access={access} {...commands} {...props} />
}

/*
 * 🔴 **인자를 여기서 짓지 않는다**(2026-09-22). 헤더의 활성 알람 배지가 **같은 질의**를
 *    쓰기 때문이다 — 인자가 한 글자라도 다르면 캐시 키가 갈려 같은 목록을 두 번 받는다.
 *    상한의 뜻과 근거는 `alarmsQuery.ts` 에 있다.
 */

function AlarmsLive(props: AlarmsContainerProps) {
  const access = useScreenAccess()
  const commands = useAlarmCommands()
  const alarms = useAlarms(ALARM_SNAPSHOT_ARGS)

  /*
   * 🔴 **손으로 지은 `.placeholder` 한 줄이었다**(2026-09-22). 다른 경계 상태와 생김새가
   *    갈라져 있었고, 새 겉모습(가운데 덩이 · 글리프)을 못 받았다. `ScreenState` 로 모은다.
   */
  if (alarms.isError) return <ScreenState text={errorMessage(alarms.error)} tone="error" />

  return (
    <AlarmsScreen
      access={access}
      loading={alarms.isPending}
      alarms={alarms.data?.items ?? []}
      // ⚠ 서버가 안 주면 `null` 이다 — 화면이 그때 카운트를 감춘다(지어내지 않는다)
      totalCount={alarms.data?.total_count ?? null}
      // ⚠ KPI 집계 엔드포인트가 계약에 없다. 화면이 합성하지 못하는 값은 **만들지 않는다**
      kpi={null}
      {...commands}
      {...props}
    />
  )
}

export interface AlarmDetailContainerProps {
  alarmId: string
  onClose: () => void
}

/**
 * 알람 상세 서랍(통합 콘솔 `/alarms/:alarmId`).
 *
 * 🔴 **`GET /alarms/{id}` 가 서버에 없다**(2026-09-28 `openapi.json` 실측 — 알람 경로는 목록 · ack · clear 셋).
 *    그래서 **목록과 같은 질의**(`ALARM_SNAPSHOT_ARGS`)에서 고른다 — 캐시 키가 같아 왕복이 늘지 않고,
 *    [확인]·[해제] 뒤 무효화가 목록과 서랍을 한 번에 다시 그린다.
 * 🔴 **네 경계**: 로딩 · 실패 · 없는 알람(받은 목록에 그 id 가 없다) · 권한 없음. `GET /alarms` 는 전 역할에
 *    열려 있어(`app/views/alarms.py` `require_roles(ROLES)`) viewer 도 서랍이 선다 — 못 하는 것은
 *    [확인]·[해제]뿐이고 그건 버튼이 비활성 + 사유로 말한다. 목록 조회가 실패하면 **서랍 안에만** 사유를 적는다.
 */
export function AlarmDetailContainer(props: AlarmDetailContainerProps) {
  return dataSourceFor('alarms') === 'demo' ? (
    <AlarmDetailDemo {...props} />
  ) : (
    <AlarmDetailLive {...props} />
  )
}

function AlarmDetailDemo({ alarmId, onClose }: AlarmDetailContainerProps) {
  const access = useScreenAccess()
  const commands = useAlarmCommands()
  return (
    <AlarmDetailScreen
      access={access}
      alarm={DEMO_ALARMS.find((a) => a.alarm_id === alarmId)}
      onClose={onClose}
      {...commands}
    />
  )
}

function AlarmDetailLive({ alarmId, onClose }: AlarmDetailContainerProps) {
  const access = useScreenAccess()
  const alarms = useAlarms(ALARM_SNAPSHOT_ARGS)
  const ack = useAckAlarm()
  const clear = useClearAlarm()
  /* 「발송」 칸의 수신자 이름 — admin 만 받는다(서버 `ALLOWED_ROLES`). 아니면 부르지 않고 채널 원문으로 선다 */
  const admin = access.permissions?.includes('settings') ?? false
  const recipients = useAlarmRecipients({ skip: !admin })

  /* 서랍 뼈대(닫기 ×)는 경계 상태에서도 선다 — 로딩·실패 중에도 닫을 수 있어야 한다 */
  if (alarms.isPending || alarms.isError) {
    return (
      <DetailPage onClose={onClose} title={t('screen.alarmDetail.title')}>
        {alarms.isError ? (
          <ScreenState text={errorMessage(alarms.error)} tone="error" />
        ) : (
          <ScreenLoading />
        )}
      </DetailPage>
    )
  }

  /* 🔴 실패는 **이 알람에 대한 것만** 보인다 — 다른 행으로 옮기면 서랍이 새로 서서(`key`) 지난 실패가 안 따라온다 */
  const failed = [ack, clear].find((m) => m.error !== null && m.variables?.alarmId === alarmId)
  return (
    <AlarmDetailScreen
      access={access}
      alarm={alarms.data.items.find((a) => a.alarm_id === alarmId)}
      onClose={onClose}
      pending={ack.isPending || clear.isPending}
      {...(failed === undefined ? {} : { commandError: errorMessage(failed.error) })}
      {...(admin && recipients.data !== undefined ? { recipients: recipients.data } : {})}
      onAck={(id) => {
        ack.mutate({ alarmId: id })
      }}
      onClear={(id) => {
        clear.mutate({ alarmId: id })
      }}
    />
  )
}
